import {useEffect, useMemo, useState} from 'react';
import type {AttachmentSimpleResponse} from 'src/api/ProactApiClient';
import {useAppSelector} from 'src/redux/hooks';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import {findBoundOfflineFile, findBoundOfflineFiles} from './files';
import {useConnectivity} from './useConnectivity';

// Resolves an attachment URL — the authed network URL synchronously, then
// overridden with a ready offline session's cached/captured blob when present.
export const useAttachmentUrl = (
  attachment: AttachmentSimpleResponse | undefined
): string | undefined => {
  const accessToken = useAppSelector(state => state.auth.accessToken);
  const networkUrl = useMemo(
    () =>
      attachment
        ? getUrlWithEncodingAndAuth(attachment.contentUrl, accessToken)
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachment?.contentUrl, accessToken]
  );
  const [url, setUrl] = useState<string | undefined>(networkUrl);
  const key = attachment?.key;

  useEffect(() => {
    setUrl(networkUrl);
    if (!key) return;
    let objectUrl: string | undefined;
    let cancelled = false;

    void findBoundOfflineFile(key).then(file => {
      if (cancelled || !file) return;
      objectUrl = URL.createObjectURL(file.blob);
      setUrl(objectUrl);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key, networkUrl]);

  return url;
};

// Batch variant for lists — same synchronous-then-blob resolution, keyed by
// each attachment's storage key.
export const useAttachmentUrls = (
  attachments: AttachmentSimpleResponse[]
): Record<string, string> => {
  const accessToken = useAppSelector(state => state.auth.accessToken);
  const keySignature = attachments.map(attachment => attachment.key).join('|');

  const networkUrls = useMemo(() => {
    const map: Record<string, string> = {};
    attachments.forEach(attachment => {
      map[attachment.key] = getUrlWithEncodingAndAuth(
        attachment.contentUrl,
        accessToken
      );
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySignature, accessToken]);

  const [urls, setUrls] = useState<Record<string, string>>(networkUrls);

  useEffect(() => {
    setUrls(networkUrls);
    let cancelled = false;
    const created: string[] = [];

    void (async () => {
      const files = await findBoundOfflineFiles(
        attachments.map(attachment => attachment.key)
      );
      if (files.size === 0) return;
      const overrides = {...networkUrls};
      files.forEach((file, key) => {
        const objectUrl = URL.createObjectURL(file.blob);
        created.push(objectUrl);
        overrides[key] = objectUrl;
      });
      if (cancelled) created.forEach(URL.revokeObjectURL);
      else setUrls(overrides);
    })();

    return () => {
      cancelled = true;
      created.forEach(objectUrl => URL.revokeObjectURL(objectUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkUrls]);

  return urls;
};

/**
 * Object URLs for cached blobs by storage key — the inline-attachment variant,
 * whose payloads are not `AttachmentSimpleResponse`. Empty while online.
 */
export const useCachedBlobUrls = (refKeys: string[]): Record<string, string> => {
  const {isOffline} = useConnectivity();
  const keySignature = refKeys.join('|');
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOffline) {
      setUrls({});
      return;
    }
    let cancelled = false;
    const created: string[] = [];

    void findBoundOfflineFiles(keySignature ? keySignature.split('|') : []).then(
      files => {
        const next: Record<string, string> = {};
        files.forEach((file, key) => {
          const objectUrl = URL.createObjectURL(file.blob);
          created.push(objectUrl);
          next[key] = objectUrl;
        });
        if (cancelled) created.forEach(URL.revokeObjectURL);
        else setUrls(next);
      }
    );

    return () => {
      cancelled = true;
      created.forEach(objectUrl => URL.revokeObjectURL(objectUrl));
    };
  }, [isOffline, keySignature]);

  return urls;
};
